import { ref, computed, defineAsyncComponent } from 'vue';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { useInteractionActions } from '@/app/community/composables/useInteractionActions';
import { useCommunityTranslationStore } from '@/stores/community-translation';
import { getLanguageName } from '@/utils/countries';
import {
  communityConversationUpdateEvent,
  communityMessageInteractionEvent,
  translationEvent,
} from '@/app/community/utils/mixpanel-events';
import {
  blockConfirmationPopup,
  COMMENT_ACTIONS,
  COMMUNITY_BLOCK_TOAST,
  COMMUNITY_COMMENT_RESOURCE_TYPES,
  COMMUNITY_INTERACTION_ACTIONS,
  COMMUNITY_INTERACTION_ACTIONS_ERROR_TOASTS,
  COMMUNITY_INTERACTION_ACTION_BUTTON_LOCATIONS,
  COMMUNITY_INTERACTION_ERROR_TOAST,
  COMMUNITY_INTERACTION_PLATFORM_BY_TYPE,
  COMMUNITY_INTERACTION_TYPES,
  COMMUNITY_SPAM_LABELS,
  COMMUNITY_UNBLOCK_TOAST,
  MESSAGE_REACTION_ERROR_MESSAGES,
  retweetActionConstants,
  THREADED_COMMENT_ACTIONS,
  MANAGE_INTEGRATIONS_LINK,
} from '@/app/community/constants';
import { useCommunityDmsStore } from '@/stores/community-dms';
import { useIdentityStore } from '@/stores/identity';
import { useCommunityCommentsStore } from '@/stores/community-comments';
import { useTicketStore } from '@/stores/ticket';
import { useConfirmationDialog, useToast } from '@dashhudson/dashing-ui';
import { HttpStatusCode } from 'axios';
import { getToastForInteractionType } from '@/app/community/utils/community-interactions';
import communityApi from '@/apis/community';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import { useRouter } from 'vue-router';

export function useInteractionMenu(target) {
  const router = useRouter();
  const interactionStore = useCommunityInteractionStore();
  const identityStore = useIdentityStore();
  const dmStore = useCommunityDmsStore();
  const commentStore = useCommunityCommentsStore();
  const ticketStore = useTicketStore();
  const { canAccessSalesforceIntegration, hasAccessToIntegrations } = useCommunityPermissions();
  const { updateSpamStatus } = useInteractionActions();

  const { addToast } = useToast({ closable: true, life: 3000 });
  const { showConfirmationDialog } = useConfirmationDialog({
    group: 'global',
    onAccept: null,
    onReject: null,
  });

  const menuTarget = computed(() => target.value ?? interactionStore.selectedCommunityInteraction);
  const messageTypeString = computed(() => {
    if (menuTarget.value.type?.includes('CONVERSATION')) {
      return 'Message';
    }
    return menuTarget.value.type?.includes('TWITTER') ? 'Post' : 'Comment';
  });

  /**
   * Tickets
   */
  const ticketLoading = ref({});

  const baseTicketMenuItems = computed(() => {
    const { message, ...interaction } = menuTarget.value;
    return ticketStore.getTicketMenuItems({ interaction, message });
  });

  const ticketMenuItems = computed(
    () =>
      baseTicketMenuItems.value?.map((item) => {
        if (item.action === 'send') {
          return {
            ...item,
            loading: ticketLoading.value[item.integrationName] || false,
            command: async (payload) => {
              ticketLoading.value = { ...ticketLoading.value, [item.integrationName]: true };
              try {
                await item.command(payload);
                if (item.integrationName === 'Salesforce' && canAccessSalesforceIntegration.value) {
                  addToast({
                    severity: 'success',
                    message: `Salesforce case created`,
                  });
                } else {
                  addToast({
                    severity: 'success',
                    message: `Message successfully sent to ${item.integrationName}`,
                  });
                }
              } catch (error) {
                if (error.message !== 'Mapping not configured') {
                  addToast({
                    severity: 'error',
                    message: `There was a problem sending this message to ${item.integrationName}.  Please try again.`,
                  });
                }
                throw error;
              } finally {
                ticketLoading.value[item.integrationName] = false;
              }
            },
          };
        }
        return item;
      }) ?? [],
  );

  /**
   * Translation
   */
  const translationStore = useCommunityTranslationStore();
  const translating = ref(false);
  const translated = ref(false);
  const translatedText = ref('');
  const translationData = computed(() => {
    const { id: messageId } = menuTarget.value.message ?? menuTarget.value;
    return translationStore.translationMap[messageId] ?? {};
  });
  const showTranslateMenu = computed(() => translationData.value?.translatable);
  async function translateText() {
    translating.value = true;
    const { id: messageId, text: displayText } = menuTarget.value.message ?? menuTarget.value;
    if (!translated.value && translatedText.value.trim() !== displayText.trim()) {
      const { detectedLanguage } = translationData.value;
      if (translatedText.value === '') {
        const result = await translationStore.translateMessage(messageId, displayText, true, true);
        translatedText.value = result;
      }
      if (translatedText.value) {
        translated.value = true;
      } else {
        translated.value = false;
        translatedText.value = '';
      }
      translationEvent(getLanguageName(detectedLanguage), menuTarget.value, false, true);
    } else if (translated.value) {
      translated.value = false;
    }
    translating.value = false;
    translationStore.toggleTranslationMapItem(messageId, translated.value);
  }

  const translateMenuItem = computed(() => {
    if (showTranslateMenu.value) {
      return [
        {
          icon: 'translate',
          label: 'Translate',
          active: translated.value,
          disabled: translating.value,
          loading: translating.value,
          command: () => translateText(),
        },
      ];
    }
    return [];
  });

  /**
   * Like
   */
  const showLikeMenu = computed(
    () =>
      [
        COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION,
        COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT,
        COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION,
        COMMUNITY_INTERACTION_TYPES.TWITTER_QUOTE,
        COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET,
        COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT,
      ].includes(menuTarget.value?.type) && !menuTarget.value.deletedAt,
  );
  const isLiked = computed(() => {
    if (menuTarget.value.type === COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION) {
      const { message, brandParticipantId } = menuTarget.value;
      return dmStore.isLikedBy(message, brandParticipantId);
    }
    return menuTarget.value.isLiked;
  });
  async function likeCommentOrMessage() {
    if (menuTarget.value.type === COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION) {
      try {
        const { message, ...interaction } = menuTarget.value;
        const actionString = isLiked.value ? 'unliked' : 'liked';
        await dmStore.likeInstagramMessage(interaction, message);
        addToast({
          message: `Message ${actionString}`,
          severity: 'success',
        });
      } catch {
        addToast({
          message: MESSAGE_REACTION_ERROR_MESSAGES.OTHER,
          severity: 'error',
        });
        return;
      }
      await interactionStore.setCommunityInteractionRead();
    } else if (!menuTarget.value.type.includes('CONVERSATION')) {
      const toLiked = !menuTarget.value.isLiked;
      const type = menuTarget.value?.type;
      const { platform, platformValue, messageType } = COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[type];

      try {
        // optimistic update
        menuTarget.value.isLiked = toLiked;
        const actionString = toLiked ? 'liked' : 'unliked';
        const result = await commentStore.updateComment({
          params: { commentId: menuTarget.value.id, platform: platformValue },
          data: { isLiked: toLiked },
        });
        addToast({
          message: `${messageTypeString.value} ${actionString}`,
          severity: 'success',
        });
        communityMessageInteractionEvent({
          platform,
          messageType,
          text: '',
          actionPerformed: toLiked
            ? COMMUNITY_INTERACTION_ACTIONS.LIKE
            : COMMUNITY_INTERACTION_ACTIONS.UNLIKE,
          communityInteraction: result?.data,
        });
      } catch {
        // undo optimistic update
        menuTarget.value.isLiked = !toLiked;
        addToast({
          message: COMMUNITY_INTERACTION_ACTIONS_ERROR_TOASTS.LIKE,
          severity: 'error',
        });
      }
    }
  }
  const likeMenuItem = computed(() => {
    if (showLikeMenu.value) {
      return [
        {
          icon: {
            name: isLiked.value ? 'heart-remove' : 'heart',
            class: isLiked.value ? 'fill-[--color-filter-red]' : undefined,
          },
          label: isLiked.value ? 'Unlike' : 'Like',
          disabled: !!menuTarget.value.deletedAt || !!menuTarget.value?.isHidden,
          command: async () => {
            await likeCommentOrMessage();
          },
        },
      ];
    }
    return [];
  });

  /**
   * Block User
   */
  async function toggleBlockState() {
    const toBlocked = !menuTarget.value.isAuthorBlocked;
    const { title, confirmMessage, confirmAlias } = blockConfirmationPopup(toBlocked);
    const confirmed = await showConfirmationDialog({
      header: title,
      message: confirmMessage,
      acceptLabel: confirmAlias,
      acceptSeverity: 'danger',
    });
    const toast = toBlocked ? COMMUNITY_BLOCK_TOAST : COMMUNITY_UNBLOCK_TOAST;
    if (confirmed) {
      try {
        // optimistic update
        menuTarget.value.isAuthorBlocked = toBlocked;
        await interactionStore.handleUserBlocked({
          interaction: menuTarget.value,
          afterUpdateHook: commentStore.updateCommentStore,
          toBlocked,
        });
        addToast({
          message: toast.SUCCESS,
          severity: 'success',
        });
      } catch (error) {
        // undo optimistic update
        menuTarget.value.isAuthorBlocked = !toBlocked;
        const errorToast =
          toBlocked && error.response?.status === HttpStatusCode.Conflict
            ? toast.ERROR.RECENTLY_BLOCKED
            : toast.ERROR.DEFAULT;
        addToast({
          message: errorToast,
          severity: 'error',
        });
      }
    }
  }
  const showBlockUserMenu = computed(() => {
    if (menuTarget.value?.type === COMMUNITY_INTERACTION_TYPES.FACEBOOK_CONVERSATION) {
      return !menuTarget.value.message?.sentByBrand;
    }
    if (menuTarget.value?.type === COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT) {
      return !menuTarget.value.isOwned;
    }
    return false;
  });
  const blockUserMenuItem = computed(() => {
    if (showBlockUserMenu.value) {
      const isBlocked = menuTarget.value.isAuthorBlocked;
      return [
        {
          icon: 'slash-circle',
          label: isBlocked ? 'Unblock User' : 'Block User',
          severity: 'danger',
          class: 'dash-text-danger dash-fill-icon-danger',
          command: async () => {
            await toggleBlockState();
          },
        },
      ];
    }
    return [];
  });

  /**
   * Hide User
   */
  async function hideUser() {
    const confirmed = await showConfirmationDialog({
      header: 'Hide this user?',
      message:
        'This will hide all existing comments and disallow any future public comments made by this author.',
      acceptLabel: 'Hide',
      acceptSeverity: 'danger',
    });

    if (confirmed) {
      const type = menuTarget.value?.type;
      const { platform, platformValue, messageType } = COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[type];
      try {
        // optimistic update
        menuTarget.value.isHidden = true;
        await commentStore.updateCommentRequest({
          commentId: menuTarget.value.id,
          platform: platformValue,
        });
        addToast({
          message: getToastForInteractionType(type, COMMUNITY_INTERACTION_ACTIONS.HIDE),
          severity: 'success',
        });
        const sourceKey = `${platformValue}CommentId`;
        commentStore.removeCommentFromStore({
          platform: platformValue,
          id: menuTarget.value.id,
          parentCommentId: menuTarget.value.parentId ?? menuTarget.value[sourceKey],
          replyToRemove: menuTarget.value.parentId ? menuTarget.value : undefined,
          mediaId: menuTarget.value.mediaId,
        });
        communityMessageInteractionEvent({
          platform,
          messageType,
          text: menuTarget.value.text,
          actionPerformed: COMMUNITY_INTERACTION_ACTIONS.HIDE,
          communityInteraction: menuTarget.value,
        });
      } catch (error) {
        // undo optimistic update
        menuTarget.value.isHidden = false;
        const apiError = error.response.data?.errors?.application?.reason;
        addToast({
          message: apiError || COMMUNITY_INTERACTION_ACTIONS_ERROR_TOASTS.HIDE_USER,
          severity: 'error',
        });
      }
    }
  }
  const showHideUserMenu = computed(
    () =>
      menuTarget.value?.type === COMMUNITY_INTERACTION_TYPES.YOUTUBE_COMMENT &&
      !menuTarget.value?.type.isOwned,
  );
  const hideUserMenuItem = computed(() => {
    if (showHideUserMenu.value) {
      return [
        {
          icon: 'eye-off',
          label: 'Hide User',
          severity: 'danger',
          class: 'dash-text-danger dash-fill-icon-danger',
          command: async () => {
            await hideUser();
          },
        },
      ];
    }
    return [];
  });

  /**
   * Hide Comment/Tweet
   */
  async function hideComment() {
    const toHidden = !menuTarget.value.isHidden;
    const { HIDE: hideError, UNHIDE: unhideError } = COMMUNITY_INTERACTION_ACTIONS_ERROR_TOASTS;
    const type = menuTarget.value?.type;
    const { platform, platformValue, messageType } = COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[type];
    const actionString = toHidden ? 'Hide' : 'Unhide';
    const messageString = toHidden ? 'will no longer be' : 'will be';
    const confirmed = await showConfirmationDialog({
      header: `${actionString} This ${messageTypeString.value}?`,
      message: `This ${messageTypeString.value.toLowerCase()} ${messageString} visible on ${platform} and will automatically be archived in Dash. You can undo these actions if needed.`,
      acceptLabel: actionString,
      acceptSeverity: 'primary',
    });

    if (confirmed) {
      const interactionInList = interactionStore.communityInteractions.find(
        (interaction) => interaction.id === menuTarget.value.id,
      );

      try {
        // optimistic update
        menuTarget.value.isHidden = toHidden;

        // Also update the interaction in the list for preview cards (so hidden badge is shown immediately)
        if (interactionInList) {
          interactionInList.isHidden = toHidden;
          if (toHidden) {
            interactionInList.isArchived = true;
          }
        }

        const result = await commentStore.updateComment({
          params: { commentId: menuTarget.value.id, platform: platformValue },
          data: { isHidden: toHidden },
        });
        communityMessageInteractionEvent({
          platform,
          messageType,
          text: '',
          actionPerformed: toHidden ? COMMENT_ACTIONS.HIDE : COMMENT_ACTIONS.UNHIDE,
          communityInteraction: result?.data,
        });
      } catch {
        // undo optimistic update
        menuTarget.value.isHidden = !toHidden;

        // Also undo the list update
        if (interactionInList) {
          interactionInList.isHidden = !toHidden;
          if (toHidden) {
            interactionInList.isArchived = false;
          }
        }

        addToast({
          message: toHidden ? hideError : unhideError,
          severity: 'error',
        });
      }
    }
  }
  const showHideCommentMenu = computed(() => {
    if (
      [
        COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT,
        COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT,
      ].includes(menuTarget.value?.type)
    ) {
      return !menuTarget.value.isOwned;
    }
    if (menuTarget.value?.type === COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION) {
      const media = interactionStore.selectedCommunityInteractionMedia;
      return (
        !menuTarget.value.isOwned &&
        !!menuTarget.value.mediaId &&
        !media?.sourceData?.inReplyToStatusId
      );
    }
    return [COMMUNITY_INTERACTION_TYPES.INSTAGRAM_COMMENT].includes(menuTarget.value?.type);
  });
  const hideCommentMenuItem = computed(() => {
    if (showHideCommentMenu.value) {
      const isHidden = menuTarget.value.isHidden;
      return [
        {
          icon: 'eye-off',
          label: isHidden ? `Unhide ${messageTypeString.value}` : `Hide ${messageTypeString.value}`,
          disabled: !!menuTarget.value.deletedAt,
          command: async () => {
            await hideComment();
          },
        },
      ];
    }
    return [];
  });

  /**
   * Update spam status of interaction
   */
  const isSpamMenuItem = computed(() => {
    const isSpam = menuTarget.value.isSpam;
    return [
      {
        icon: isSpam ? 'remove-circle' : 'message-x',
        label: isSpam ? COMMUNITY_SPAM_LABELS.MARK_AS_NOT_SPAM : COMMUNITY_SPAM_LABELS.MARK_AS_SPAM,
        class: isSpam ? 'dash-text-danger dash-fill-icon-danger' : null,
        command: async () => {
          const newSpamValue = !isSpam;
          updateSpamStatus(menuTarget.value.id, newSpamValue, menuTarget.value);

          const { platform, messageType } =
            COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[menuTarget.value.type] || {};
          const action = newSpamValue
            ? COMMUNITY_INTERACTION_ACTIONS.MARK_AS_SPAM
            : COMMUNITY_INTERACTION_ACTIONS.MARK_AS_NOT_SPAM;

          communityConversationUpdateEvent({
            platform,
            messageType,
            action,
            location: COMMUNITY_INTERACTION_ACTION_BUTTON_LOCATIONS.PANEL,
            originalCommunityInteraction: menuTarget.value,
            updatedCommunityInteraction: { ...menuTarget.value, isSpam: newSpamValue },
          });
        },
      },
    ];
  });

  /**
   * Delete Comment/Tweet
   */
  async function deleteComment() {
    const { type } = menuTarget.value;
    const { platform, platformValue, messageType } = COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[type];
    const confirmed = await showConfirmationDialog({
      header: `Delete This ${messageTypeString.value}?`,
      message: `This ${messageTypeString.value.toLowerCase()} will be deleted from ${platform} which cannot be undone. It will automatically be archived in Dash.`,
      acceptLabel: 'Delete',
      acceptSeverity: 'danger',
    });

    if (confirmed) {
      const interactionInList = interactionStore.communityInteractions.find(
        (interaction) => interaction.id === menuTarget.value.id,
      );

      try {
        menuTarget.value.deletedAt = new Date().toDateString();

        if (interactionInList) {
          interactionInList.isDeleted = true;
          interactionInList.isArchived = true;
        }

        await commentStore.deleteComment({
          commentId: menuTarget.value.id,
          platform: platformValue,
        });
        addToast({
          message: getToastForInteractionType(type, COMMUNITY_INTERACTION_ACTIONS.DELETE),
          severity: 'success',
        });
        communityMessageInteractionEvent({
          platform,
          messageType,
          text: '',
          actionPerformed: COMMUNITY_INTERACTION_ACTIONS.DELETE,
          communityInteraction: menuTarget.value,
        });
      } catch (error) {
        menuTarget.value.deletedAt = null;

        if (interactionInList) {
          interactionInList.isDeleted = false;
          interactionInList.isArchived = false;
        }

        const apiError = error.response.data?.errors?.application?.reason;
        addToast({
          message: apiError || COMMUNITY_INTERACTION_ERROR_TOAST,
          severity: 'error',
        });
      }
    }
  }
  const showDeleteCommentMenu = computed(() => {
    const isDeleted = !!menuTarget.value.deletedAt;
    if (
      [
        COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT,
        COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION,
      ].includes(menuTarget.value?.type)
    ) {
      return menuTarget.value.isOwned && !isDeleted;
    }
    return (
      [
        COMMUNITY_INTERACTION_TYPES.INSTAGRAM_COMMENT,
        COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT,
        COMMUNITY_INTERACTION_TYPES.YOUTUBE_COMMENT,
      ].includes(menuTarget.value?.type) && !isDeleted
    );
  });
  const deleteCommentMenuItem = computed(() => {
    if (showDeleteCommentMenu.value) {
      return [
        {
          icon: 'delete',
          label: `Delete ${messageTypeString.value}`,
          severity: 'danger',
          class: 'dash-text-danger dash-fill-icon-danger',
          disabled: !!menuTarget.value.deletedAt,
          command: async () => {
            await deleteComment();
          },
        },
      ];
    }
    return [];
  });

  /**
   * Repost & Quote
   */
  async function repostTweet() {
    const toRetweeted = !menuTarget.value.isRetweeted;
    const { confirmAlias, confirmMessage, successMessage, errorMessage } =
      retweetActionConstants(toRetweeted);

    const confirmed = await showConfirmationDialog({
      group: 'global',
      header: confirmMessage,
      acceptLabel: confirmAlias,
      acceptSeverity: 'primary',
    });

    if (confirmed) {
      const type = menuTarget.value?.type;
      const { platform, platformValue, messageType } = COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[type];

      try {
        // optimistic update
        menuTarget.value.isRetweeted = toRetweeted;
        const result = await commentStore.updateComment({
          params: { commentId: menuTarget.value.id, platform: platformValue },
          data: { isRetweeted: toRetweeted },
        });
        addToast({ message: successMessage, severity: 'success' });
        communityMessageInteractionEvent({
          platform,
          messageType,
          text: '',
          actionPerformed: toRetweeted
            ? THREADED_COMMENT_ACTIONS.RETWEET
            : THREADED_COMMENT_ACTIONS.REMOVE_RETWEET,
          communityInteraction: result?.data,
        });
      } catch (error) {
        // undo optimistic update
        menuTarget.value.isRetweeted = !toRetweeted;
        const apiError = error.response.data?.errors?.application?.reason;
        addToast({
          message: apiError || errorMessage,
          severity: 'error',
        });
      }
    }
  }
  async function quoteTweet(quoteMessage) {
    const type = menuTarget.value?.type;
    const { platform, messageType } = COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[type];
    try {
      await communityApi.createTweet({
        brandId: menuTarget.value.brandId,
        parentTweetId: menuTarget.value.twitterTweetId,
        text: quoteMessage,
        isQuote: true,
      });
      addToast({ message: 'Successfully published', severity: 'success' });
      communityMessageInteractionEvent({
        platform,
        messageType,
        text: '',
        actionPerformed: COMMUNITY_INTERACTION_ACTIONS.POSTED,
        communityInteraction: menuTarget.value,
      });
    } catch (error) {
      const apiError = error.response.data?.errors?.application?.reason;
      addToast({
        message: apiError || 'Failed to publish. Please try again.',
        severity: 'error',
      });
    }
  }
  const showRepostTweetMenu = computed(
    () =>
      [
        COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION,
        COMMUNITY_INTERACTION_TYPES.TWITTER_QUOTE,
      ].includes(menuTarget.value?.type) && !menuTarget.value.deletedAt,
  );
  const repostMenuItem = computed(() => {
    if (showRepostTweetMenu.value) {
      return [
        {
          icon: 'retweet',
          label: 'Repost',
          id: `repost-menu-${menuTarget.value.id}`,
          disabled: !!menuTarget.value.isHidden,
          active: menuTarget.value.isRetweeted || menuTarget.value.isQuotedBy?.length > 0,
        },
      ];
    }
    return [];
  });
  const repostNestedMenuItems = computed(() => {
    if (showRepostTweetMenu.value) {
      const { isRetweeted } = menuTarget.value ?? {};
      return [
        {
          icon: isRetweeted ? 'refresh-ccw' : 'retweet',
          label: isRetweeted ? 'Undo Repost' : 'Repost',
          command: async () => {
            await repostTweet();
          },
        },
        {
          icon: 'draft',
          label: 'Quote Post',
          form: defineAsyncComponent(
            () => import('@/app/community/components/CreateQuotePostForm.vue'),
          ),
          formProps: {
            post: menuTarget.value,
          },
          command: async (quoteMessage) => {
            await quoteTweet(quoteMessage);
          },
        },
      ];
    }
    return [];
  });

  /**
   * Internal Message Menu
   */
  const showInternalMessageMenu = computed(
    () =>
      menuTarget.value?.message?.resourceType !==
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT,
  );

  const internalMessageMenu = computed(() => {
    if (showInternalMessageMenu.value) {
      const dropdownOptions = menuTarget.value?.message?.props?.dropdownOptions;
      return {
        menuItems: dropdownOptions,
        numMenuButtons: 0,
      };
    }
    return [];
  });

  const conversationMessageMenu = computed(() => {
    let numMenuButtons = 0;
    if (showTranslateMenu.value) {
      numMenuButtons += 1;
    }
    if (showLikeMenu.value) {
      numMenuButtons += 1;
    }
    const menuItems = [
      ...translateMenuItem.value,
      ...likeMenuItem.value,
      ...blockUserMenuItem.value,
      ...ticketMenuItems.value,
    ];
    if (
      canAccessSalesforceIntegration.value &&
      hasAccessToIntegrations.value &&
      identityStore.checkIfUserIsAdminForBrand(menuTarget.value?.brandId)
    ) {
      menuItems.push({
        link: MANAGE_INTEGRATIONS_LINK,
        command: () => {
          router.push({ name: 'settings.integrations' });
        },
      });
    }
    return {
      menuItems,
      numMenuButtons,
    };
  });

  const conversationHiddenMenu = computed(() => [...isSpamMenuItem.value]);

  const commentHiddenMenu = computed(() => {
    const menuItems = [
      ...hideCommentMenuItem.value,
      ...isSpamMenuItem.value,
      ...deleteCommentMenuItem.value,
      ...ticketMenuItems.value,
      ...blockUserMenuItem.value,
      ...hideUserMenuItem.value,
    ];
    if (
      canAccessSalesforceIntegration.value &&
      hasAccessToIntegrations.value &&
      identityStore.checkIfUserIsAdminForBrand(menuTarget.value?.brandId)
    ) {
      menuItems.push({
        link: MANAGE_INTEGRATIONS_LINK,
        command: () => {
          router.push({ name: 'settings.integrations' });
        },
      });
    }
    return menuItems;
  });

  const commentButtonMenu = computed(() => {
    let numMenuButtons = 0;
    if (showTranslateMenu.value) {
      numMenuButtons += 1;
    }
    if (showRepostTweetMenu.value) {
      numMenuButtons += 1;
    }
    if (showLikeMenu.value) {
      numMenuButtons += 1;
    }
    return {
      menuItems: [...translateMenuItem.value, ...repostMenuItem.value, ...likeMenuItem.value],
      numMenuButtons,
    };
  });

  return {
    ticketMenuItems,
    translating,
    translated,
    translatedText,
    translationData,
    translateText,
    showTranslateMenu,
    translateMenuItem,
    showLikeMenu,
    likeMenuItem,
    showRepostTweetMenu,
    repostMenuItem,
    repostNestedMenuItems,
    conversationMessageMenu,
    internalMessageMenu,
    conversationHiddenMenu,
    commentHiddenMenu,
    commentButtonMenu,
  };
}
